/**
 * fiko-toporef.ts — MERKEZİ TOPOLOJİK REFERANS (B-Rep link) çözücüsü.
 *
 * Part Design + GSD feature'ları bir yüz/kenarı index ile değil, GEOMETRİK İMZA ile
 * referanslar (CATIA/NX persistent-naming yaklaşımı). İmza = merkez + yön/normal + ölçü.
 * Rebuild sonrası faceId/kenar sırası kaysa veya parametre değişip yüz/kenar HAFİFÇE
 * kaysa bile en iyi eşleşme skorla bulunur → link kopmaz ("her şey breple").
 *
 * Tüm matematik tahsissiz (zero-alloc): saf sayı dizileri, Vector3 üretmez.
 */

/** Bir yüz/kenarın kalıcı topolojik imzası (saklanır + serialize edilir). */
export interface ITopoImza {
  merkez: [number, number, number];    // yüz merkezi / kenar orta noktası (dünya)
  yon?: [number, number, number];      // kenar yönü (normalize) / yüz normali
  olcu?: number;                       // kenar uzunluğu / yüz alanı (ölçek-bağımsız ayırt edici)
}

/** İki imza arası eşleşme skoru (0..1). olcek = model köşegeni (tolerans normalizasyonu). */
export function topoSkor(saklanan: ITopoImza, aday: ITopoImza, olcek: number): number {
  const o = olcek > 1e-6 ? olcek : 1;
  // 1) Konum yakınlığı (model ölçeğine göre normalize) — en ağır kriter
  const dx = saklanan.merkez[0] - aday.merkez[0];
  const dy = saklanan.merkez[1] - aday.merkez[1];
  const dz = saklanan.merkez[2] - aday.merkez[2];
  const mesafe = Math.sqrt(dx * dx + dy * dy + dz * dz) / o;
  const sKonum = 1 / (1 + 30 * mesafe * mesafe);   // 0 mesafe → 1, uzaklaştıkça hızlı düşer
  // 2) Yön/normal hizalanması (varsa) — ±yön eşdeğer (|cos|)
  let sYon = 1;
  if (saklanan.yon && aday.yon) {
    const d = saklanan.yon[0] * aday.yon[0] + saklanan.yon[1] * aday.yon[1] + saklanan.yon[2] * aday.yon[2];
    sYon = Math.abs(d);   // 1 = aynı/zıt eksen, 0 = dik
  }
  // 3) Ölçü (uzunluk/alan) benzerliği (varsa)
  let sOlcu = 1;
  if (saklanan.olcu != null && aday.olcu != null) {
    const a = Math.max(1e-6, saklanan.olcu), b = Math.max(1e-6, aday.olcu);
    sOlcu = Math.min(a, b) / Math.max(a, b);   // oran (0..1)
  }
  return sKonum * (0.55 + 0.3 * sYon + 0.15 * sOlcu);   // konum baskın, yön+ölçü ayırt edici
}

/**
 * Saklanan imzaları aday listesine eşler → eşleşen adayların id'leri (skor ≥ esik).
 * Her saklanan imza için EN İYİ aday seçilir; bir aday iki kez seçilmez (greedy en-yüksek-skor).
 */
export function topoCoz(
  saklananlar: ITopoImza[],
  adaylar: { id: number; imza: ITopoImza }[],
  olcek: number,
  esik = 0.35,
): number[] {
  if (!saklananlar.length || !adaylar.length) return [];
  const ciftler: { s: number; si: number; ai: number }[] = [];
  for (let si = 0; si < saklananlar.length; si++)
    for (let ai = 0; ai < adaylar.length; ai++) {
      const s = topoSkor(saklananlar[si], adaylar[ai].imza, olcek);
      if (s >= esik) ciftler.push({ s, si, ai });
    }
  ciftler.sort((x, y) => y.s - x.s);   // en yüksek skor önce
  const sKul = new Set<number>(), aKul = new Set<number>(), sonuc: number[] = [];
  for (const c of ciftler) {
    if (sKul.has(c.si) || aKul.has(c.ai)) continue;
    sKul.add(c.si); aKul.add(c.ai); sonuc.push(adaylar[c.ai].id);
  }
  return sonuc;
}

/** Aday imza, saklananlardan HERHANGİ biriyle eşik üstü eşleşiyor mu? (çoka-bir: tüm dairesel
 *  kenar segmentleri tek seçili kenara eşleşebilir — kenar filtreleme için doğru semantik). */
export function topoEslesirMi(saklananlar: ITopoImza[], aday: ITopoImza, olcek: number, esik = 0.35): boolean {
  for (let i = 0; i < saklananlar.length; i++) if (topoSkor(saklananlar[i], aday, olcek) >= esik) return true;
  return false;
}

/** Düz [x,y,z,...] dizisini imza listesine çevirir (eski secKenarlar/secYuzNok ile uyum). */
export function noktalardanImza(duz: number[] | undefined): ITopoImza[] {
  const out: ITopoImza[] = [];
  if (!duz) return out;
  for (let i = 0; i + 2 < duz.length; i += 3) out.push({ merkez: [duz[i], duz[i + 1], duz[i + 2]] });
  return out;
}

/**
 * PERSISTENT NAMING durumu (TNaming benzeri): yüz kalıcı kimliği.
 * `yuzId[faceId]` = o yüzün kalıcı stableId'si. Rebuild her seferinde tabandan deterministik
 * replay yaptığı + OCCT history (Modified/Generated) ile taşındığı için bir fiziksel yüzün
 * stableId'si rebuild'ler arası SABİT kalır → radius/op eklense de seçim doğru yüze bağlı kalır.
 * Sadece veri taşıyıcı; propagasyon mantığı kernel'de (OCCT erişimi orada).
 */
export class FikoTopoKimlik {
  yuzId: number[] = [];   // faceId (TopExp sırası) → stableId
  private sonraki = 1;
  tazeId(): number { return this.sonraki++; }
  sifirla(): void { this.yuzId = []; this.sonraki = 1; }
}
